import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, trackClick } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import BookmakerCard from "@/components/BookmakerCard";
import {
  ArrowRight, Bot, Gift, HeartHandshake, MessageCircle, Send, Sparkles,
  Users, Instagram, Trophy, Dices, Zap, Gamepad2, Briefcase, LifeBuoy,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const FAQS = [
  { q: "Vinko è un bookmaker?", a: "No. Vinko è un assistente virtuale indipendente che presenta promozioni, servizi e link di registrazione dei bookmaker partner." },
  { q: "La registrazione è gratuita?", a: "L'accesso al sito, al bot e al canale Telegram gratuito non prevede costi. Eventuali depositi vengono effettuati esclusivamente sui siti ufficiali dei bookmaker." },
  { q: "Vinko gestisce il denaro degli utenti?", a: "No. Vinko non riceve e non gestisce depositi, prelievi o giocate." },
  { q: "Posso ricevere assistenza?", a: "Sì. Puoi contattare Vinko tramite il bot Telegram o WhatsApp." },
  { q: "Come posso collaborare?", a: "Compila il modulo nella sezione Lavora con noi oppure contatta Vinko tramite WhatsApp. La candidatura verrà valutata dal team." },
  { q: "È necessario avere una partita IVA?", a: "Dipende dal tipo e dalla continuità della collaborazione. Gli aspetti fiscali e contrattuali vengono valutati prima dell'inizio dell'attività." },
  { q: "Le promozioni sono sempre disponibili?", a: "Le promozioni possono essere modificate, sospese o terminate dai bookmaker. È necessario consultare sempre i termini ufficiali." },
  { q: "Dove posso trovare i link ufficiali?", a: "Tutti i link ufficiali di registrazione sono disponibili nella sezione Bookmaker e nella sezione Promozioni." },
];

const BOT_CATEGORIES = [
  { icon: Dices, label: "Casinò" },
  { icon: Trophy, label: "Sport" },
  { icon: Zap, label: "Virtual" },
  { icon: Gamepad2, label: "Slot & Games" },
  { icon: Sparkles, label: "Vinko VIP" },
  { icon: LifeBuoy, label: "Assistenza" },
  { icon: Briefcase, label: "Lavora con noi" },
];

export default function Home() {
  const s = useSettings();
  const [bookmakers, setBookmakers] = useState([]);

  useEffect(() => {
    api.get("/bookmakers").then((r) => setBookmakers(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-yellow-400/20 blur-3xl" />
          <div className="absolute top-1/2 -left-40 h-80 w-80 rounded-full bg-yellow-500/10 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 pt-10 pb-20 lg:pt-16 lg:pb-28">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
            <div className="animate-fade-up">
              <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-yellow-400 bg-yellow-400/10 border border-yellow-400/25 px-3 py-1.5 rounded-full">
                <Sparkles className="w-3 h-3" /> Assistente virtuale
              </span>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.02]">
                Tutto il mondo delle{" "}
                <span className="text-yellow-400">scommesse</span>
                <br /> in un unico posto.
              </h1>
              <p className="mt-5 max-w-xl text-base sm:text-lg text-zinc-300 leading-relaxed">
                Scopri le promozioni dei bookmaker partner, registrati attraverso
                i link ufficiali e ricevi assistenza direttamente da Vinko.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
                <Link
                  to="/promozioni"
                  data-testid="hero-cta-promo"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-sm px-6 py-3 transition-transform duration-200 hover:scale-105 yellow-glow-strong"
                >
                  SCOPRI LE PROMO <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href={s.telegram_bot_url}
                  target="_blank"
                  rel="noopener"
                  onClick={() => trackClick("telegram_bot")}
                  data-testid="hero-cta-bot"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-yellow-400/60 hover:border-yellow-400 hover:bg-yellow-400/10 text-yellow-400 font-bold text-sm px-6 py-3 transition-colors duration-200"
                >
                  <Bot className="w-4 h-4" /> ACCEDI AL BOT
                </a>
                <a
                  href={s.telegram_free_url}
                  target="_blank"
                  rel="noopener"
                  onClick={() => trackClick("telegram_free")}
                  data-testid="hero-cta-channel"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 hover:border-white/40 text-white font-bold text-sm px-6 py-3 transition-colors duration-200"
                >
                  <Send className="w-4 h-4" /> ENTRA NEL CANALE GRATUITO
                </a>
              </div>
              <p className="mt-4 text-xs text-zinc-500">
                Registrazione gratuita. Servizio riservato ai maggiori di 18 anni.
              </p>
            </div>

            <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
              <div className="relative mx-auto max-w-sm">
                <div className="absolute -inset-6 rounded-[3rem] bg-yellow-400/10 blur-2xl" />
                <div className="relative rounded-[2.2rem] border border-white/10 bg-gradient-to-b from-[#151515] to-[#0a0a0a] p-4 shadow-2xl">
                  <div className="rounded-[1.6rem] bg-[#0e0e0e] border border-white/5 overflow-hidden">
                    <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
                      <div className="h-9 w-9 rounded-full bg-yellow-400 text-black grid place-items-center font-black">V</div>
                      <div>
                        <div className="text-sm font-semibold">Vinko Bot</div>
                        <div className="text-xs text-zinc-500">online · Telegram</div>
                      </div>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="max-w-[80%] rounded-2xl rounded-tl-sm bg-white/5 border border-white/10 px-3 py-2 text-sm">
                        Ciao! Cosa vuoi scoprire oggi?
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {BOT_CATEGORIES.slice(0, 6).map(({ icon: Ic, label }) => (
                          <div key={label} className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2 text-xs flex items-center gap-2">
                            <Ic className="w-3.5 h-3.5 text-yellow-400" />
                            {label}
                          </div>
                        ))}
                      </div>
                      <div className="max-w-[80%] ml-auto rounded-2xl rounded-tr-sm bg-yellow-400 text-black font-semibold px-3 py-2 text-sm">
                        Mostrami le promo!
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COS'È VINKO */}
      <section className="relative py-16 sm:py-20 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-500">Chi siamo</span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              Il tuo <span className="text-yellow-400">assistente virtuale</span>
            </h2>
            <p className="mt-5 text-zinc-300 leading-relaxed">
              Vinko è un assistente virtuale che ti permette di scoprire bookmaker,
              promozioni, bonus, servizi e novità dedicate al mondo delle scommesse
              sportive e del casinò online. Attraverso il sito e il bot Telegram
              puoi trovare i link ufficiali di registrazione, ricevere supporto e
              accedere gratuitamente alla community Vinko.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              { icon: Gift, title: "Promozioni disponibili", text: "Scopri le offerte presenti sui bookmaker partner." },
              { icon: HeartHandshake, title: "Registrazione assistita", text: "Ricevi supporto durante la registrazione e l'attivazione delle promozioni." },
              { icon: Users, title: "Community Vinko", text: "Entra nei canali ufficiali e resta aggiornato sulle novità." },
            ].map(({ icon: Ic, title, text }) => (
              <div
                key={title}
                className="group rounded-2xl border border-white/10 bg-[#101010] p-6 transition-colors duration-200 hover:border-yellow-400/40"
              >
                <div className="h-11 w-11 rounded-xl bg-yellow-400/10 border border-yellow-400/25 grid place-items-center text-yellow-400 mb-4">
                  <Ic className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-semibold">{title}</h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOOKMAKER */}
      <section className="relative py-16 sm:py-20 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-500">Partner</span>
              <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
                Scegli il <span className="text-yellow-400">bookmaker</span>
              </h2>
              <p className="mt-3 max-w-2xl text-zinc-400">
                Consulta le promozioni e registrati attraverso i link ufficiali Vinko.
              </p>
            </div>
            <Link
              to="/bookmaker"
              data-testid="home-see-all-bookmakers"
              className="inline-flex items-center gap-2 text-yellow-400 hover:text-yellow-300 text-sm font-semibold"
            >
              Vedi tutti <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {bookmakers.map((bm) => (
              <BookmakerCard key={bm.id} bm={bm} />
            ))}
          </div>
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section className="relative py-16 sm:py-20 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-500">Facile e veloce</span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              Inizia in <span className="text-yellow-400">pochi passaggi</span>
            </h2>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              { n: "01", title: "Scegli il bookmaker", text: "Consulta le promozioni disponibili e scegli il partner più adatto." },
              { n: "02", title: "Usa il link Vinko", text: "Clicca sul pulsante di registrazione presente sul sito." },
              { n: "03", title: "Completa la registrazione", text: "Inserisci i dati richiesti dal bookmaker e completa la verifica." },
              { n: "04", title: "Ricevi assistenza", text: "Contatta Vinko attraverso Telegram o WhatsApp in caso di necessità." },
            ].map((s) => (
              <div
                key={s.n}
                className="relative rounded-2xl border border-white/10 bg-[#101010] p-6"
              >
                <div className="text-yellow-400 text-4xl font-black tracking-tighter mb-3">{s.n}</div>
                <h3 className="text-lg font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <a
              href={s.whatsapp_url}
              target="_blank"
              rel="noopener"
              onClick={() => trackClick("whatsapp")}
              data-testid="hf-whatsapp"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-sm px-6 py-3 transition-transform duration-200 hover:scale-[1.02]"
            >
              <MessageCircle className="w-4 h-4" /> RICHIEDI ASSISTENZA SU WHATSAPP
            </a>
            <a
              href={s.telegram_bot_url}
              target="_blank"
              rel="noopener"
              onClick={() => trackClick("telegram_bot")}
              data-testid="hf-bot"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 hover:border-yellow-400 hover:text-yellow-400 text-white font-bold text-sm px-6 py-3 transition-colors duration-200"
            >
              <Bot className="w-4 h-4" /> APRI IL BOT TELEGRAM
            </a>
          </div>
        </div>
      </section>

      {/* BOT TELEGRAM */}
      <section className="relative py-16 sm:py-20 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-500">Telegram</span>
              <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
                Tutto direttamente su <span className="text-yellow-400">Telegram</span>
              </h2>
              <p className="mt-5 text-zinc-300 leading-relaxed">
                Accedi al bot Vinko per consultare bookmaker, promozioni, sport,
                casinò, virtual, slot, contenuti e servizi disponibili.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={s.telegram_bot_url}
                  target="_blank"
                  rel="noopener"
                  onClick={() => trackClick("telegram_bot")}
                  data-testid="bot-cta-primary"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-sm px-6 py-3 transition-transform duration-200 hover:scale-[1.02]"
                >
                  <Bot className="w-4 h-4" /> AVVIA IL BOT VINKO
                </a>
                <a
                  href={s.telegram_free_url}
                  target="_blank"
                  rel="noopener"
                  onClick={() => trackClick("telegram_free")}
                  data-testid="bot-cta-channel"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 hover:border-yellow-400 hover:text-yellow-400 text-white font-bold text-sm px-6 py-3 transition-colors duration-200"
                >
                  <Send className="w-4 h-4" /> ENTRA NEL CANALE GRATUITO
                </a>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#101010] p-6 sm:p-8">
              <div className="grid grid-cols-2 gap-3">
                {BOT_CATEGORIES.map(({ icon: Ic, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/40 px-4 py-3 hover:border-yellow-400/40 transition-colors duration-200"
                  >
                    <div className="h-9 w-9 rounded-lg bg-yellow-400/10 border border-yellow-400/25 grid place-items-center text-yellow-400">
                      <Ic className="w-4 h-4" />
                    </div>
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMMUNITY QUICK */}
      <section className="relative py-16 sm:py-20 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-500">Community</span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
            Entra nella <span className="text-yellow-400">community Vinko</span>
          </h2>

          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <a href={s.instagram_url} target="_blank" rel="noopener" onClick={() => trackClick("instagram")} data-testid="community-ig" className="rounded-2xl border border-white/10 bg-[#101010] p-6 hover:border-yellow-400/40 transition-colors duration-200">
              <Instagram className="w-6 h-6 text-yellow-400 mb-3" />
              <div className="font-semibold">Instagram</div>
              <p className="text-sm text-zinc-400 mt-1">Novità, contenuti e aggiornamenti.</p>
            </a>
            <a href={s.telegram_bot_url} target="_blank" rel="noopener" onClick={() => trackClick("telegram_bot")} data-testid="community-bot" className="rounded-2xl border border-white/10 bg-[#101010] p-6 hover:border-yellow-400/40 transition-colors duration-200">
              <Bot className="w-6 h-6 text-yellow-400 mb-3" />
              <div className="font-semibold">Telegram Bot</div>
              <p className="text-sm text-zinc-400 mt-1">Assistente virtuale Vinko.</p>
            </a>
            <a href={s.telegram_free_url} target="_blank" rel="noopener" onClick={() => trackClick("telegram_free")} data-testid="community-free" className="rounded-2xl border border-white/10 bg-[#101010] p-6 hover:border-yellow-400/40 transition-colors duration-200">
              <Send className="w-6 h-6 text-yellow-400 mb-3" />
              <div className="font-semibold">Telegram gratuito</div>
              <p className="text-sm text-zinc-400 mt-1">Aggiornamenti e contenuti gratuiti.</p>
            </a>
            <a href={s.whatsapp_url} target="_blank" rel="noopener" onClick={() => trackClick("whatsapp")} data-testid="community-wa" className="rounded-2xl border border-white/10 bg-[#101010] p-6 hover:border-yellow-400/40 transition-colors duration-200">
              <MessageCircle className="w-6 h-6 text-yellow-400 mb-3" />
              <div className="font-semibold">WhatsApp</div>
              <p className="text-sm text-zinc-400 mt-1">Assistenza diretta Vinko.</p>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-16 sm:py-20 border-t border-white/5" id="faq">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-500">FAQ</span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
            Domande <span className="text-yellow-400">frequenti</span>
          </h2>

          <Accordion type="single" collapsible className="mt-8">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`i-${i}`}
                className="border-b border-white/10"
                data-testid={`faq-${i}`}
              >
                <AccordionTrigger className="text-left text-white hover:text-yellow-400 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-400 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
